import React, { useState, useRef, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { ProgressBar } from 'primereact/progressbar';
import { DataView } from 'primereact/dataview';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toolbar } from 'primereact/toolbar';
import { Badge } from 'primereact/badge';
import { Image } from 'primereact/image';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';
import { Panel } from 'primereact/panel';
import { Message } from 'primereact/message';
import './imageGallery.css';

import ImageService from '../../service/ImageService';

const imageService = new ImageService();

const ImageGallery = () => {
    const iss = localStorage.getItem('iss');

    const [selectedFolder, setSelectedFolder] = useState('');
    const [uploadFolder, setUploadFolder] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [galleryImages, setGalleryImages] = useState([]);
    const [selectedGalleryImages, setSelectedGalleryImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const toast = useRef(null);
    const fileUploadRef = useRef(null);

    const folders = [
        { label: 'Produtos', value: 'produtos' },
        { label: 'Categoria de Produtos', value: 'categoria-produtos' },
        { label: 'Subcategoria de Produtos', value: 'subcategoria-produtos' }
    ];

    // Carrega imagens quando seleciona uma pasta
    useEffect(() => {
        if (selectedFolder) {
            loadGalleryImages();
        } else {
            setGalleryImages([]);
            setSelectedGalleryImages([]);
        }
    }, [selectedFolder]);

    // Função para carregar imagens da galeria
    const loadGalleryImages = async () => {
        if (!selectedFolder) return;

        setLoading(true);
        setError('');
        setSelectedGalleryImages([]);

        try {
            const response = await imageService.listImages(iss, selectedFolder);

            if (response.status !== 200) {
                throw new Error(`Erro ${response.status}: ${response.statusText}`);
            }

            const data = await response.data;

            if (data.success) {
                setGalleryImages(data.data || []);
                toast.current.show({
                    severity: 'success',
                    summary: 'Sucesso',
                    detail: `${data.data?.length || 0} imagens carregadas`,
                    life: 3000
                });
            } else {
                throw new Error(data.message || 'Erro ao carregar imagens');
            }
        } catch (error) {
            console.error('Erro ao carregar galeria:', error);
            setError(error.message || 'Erro ao carregar imagens da galeria');
            setGalleryImages([]);
            toast.current.show({
                severity: 'error',
                summary: 'Erro',
                detail: error.message,
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    // Função para selecionar arquivo
    const onFileSelect = (e) => {
        const files = Array.from(e.files);
        setSelectedFiles(files);
    };

    // Função para fazer upload
    const handleUpload = async () => {
        if (!uploadFolder) {
            toast.current.show({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione uma pasta para upload',
                life: 3000
            });
            return;
        }

        if (selectedFiles.length === 0) {
            toast.current.show({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione pelo menos uma imagem',
                life: 3000
            });
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        const formData = new FormData();
        selectedFiles.forEach(file => {
            formData.append('images', file);
        });
        formData.append('folder', uploadFolder);

        try {
            /* const response = await fetch(`${process.env.REACT_APP_API_URL}/api/images/upload-multiple`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formData
            }); */

            const response = await imageService.uploadMultiple(iss, formData);

            if (response.status !== 200) {
                throw new Error(`Erro ${response.status}: ${response.statusText}`);
            }

            const data = await response.data;

            //const data = await response.json();

            if (response.status === 200 && data.success) {
                const uploadedFiles = data.uploaded.map(item => item.filename || item.path).join(', ');
                const message = data.uploaded.length <= 5
                    ? `Arquivos enviados: ${uploadedFiles}`
                    : `${data.uploaded.length} imagens enviadas com sucesso`;

                toast.current.show({
                    severity: 'success',
                    summary: 'Sucesso',
                    detail: message,
                    life: 3000
                });

                setSelectedFiles([]);
                fileUploadRef.current.clear();

                // Recarrega a galeria se estiver na mesma pasta
                if (selectedFolder === uploadFolder) {
                    loadGalleryImages();
                }
            } else {
                throw new Error(data.message || 'Erro no upload');
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Erro',
                detail: error.message,
                life: 5000
            });
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // Função para deletar imagens selecionadas
    const handleDeleteSelected = () => {
        if (selectedGalleryImages.length === 0) {
            toast.current.show({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione pelo menos uma imagem para excluir',
                life: 3000
            });
            return;
        }

        confirmDialog({
            message: `Deseja realmente excluir ${selectedGalleryImages.length} imagem(ns)?`,
            header: 'Confirmação de Exclusão',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {

                    const images = JSON.stringify({
                        filenames: selectedGalleryImages.map(img => img.key)
                    })

                    const response = await imageService.deleteMultiple(iss, images)

                    /* const response = await fetch(`${process.env.REACT_APP_API_URL}/api/images/delete-multiple`, {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        },
                        body: JSON.stringify({
                            keys: selectedGalleryImages.map(img => img.key)
                        })
                    }); */

                    const data = await response.data;

                    if (response.status === 200 && data.success) {
                        // Versão com lista de arquivos (para poucos arquivos)
                        const deletedFiles = data.deleted.map(item => item.filename || item.path).join(', ');
                        const message = data.deleted.length <= 3
                            ? `Arquivos excluídos: ${deletedFiles}`
                            : `${data.deleted.length} imagens excluídas com sucesso`;

                        toast.current.show({
                            severity: 'success',
                            summary: 'Sucesso',
                            detail: message,
                            life: 3000
                        });

                        setSelectedGalleryImages([]);
                        loadGalleryImages();
                    } else {
                        throw new Error(data.message || 'Erro ao excluir');
                    }
                } catch (error) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Erro',
                        detail: error.message,
                        life: 5000
                    });
                }
            }
        });
    };

    // Função para deletar uma imagem
    const handleDeleteSingle = (image) => {
        confirmDialog({
            message: `Deseja realmente excluir "${image.name}"?`,
            header: 'Confirmação de Exclusão',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const response = await imageService.deleteMultiple(iss, { filenames: [image.key] })

                    const data = await response.data;

                    if (response.status === 200 && data.success) {
                        // Versão com lista de arquivos (para poucos arquivos)
                        const deletedFiles = data.deleted.map(item => item.filename || item.path).join(', ');
                        const message = data.deleted.length <= 3
                            ? `Arquivo excluído: ${deletedFiles}`
                            : `${data.deleted.length} imagens excluídas com sucesso`;

                        toast.current.show({
                            severity: 'success',
                            summary: 'Sucesso',
                            detail: message,
                            life: 3000
                        });

                        loadGalleryImages();
                    } else {
                        throw new Error(data.message || 'Erro ao excluir');
                    }
                } catch (error) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Erro',
                        detail: error.message,
                        life: 5000
                    });
                }
            }
        });
    };

    // Toggle seleção de imagem
    const toggleImageSelection = (image) => {
        const isSelected = selectedGalleryImages.some(img => img.key === image.key);

        if (isSelected) {
            setSelectedGalleryImages(selectedGalleryImages.filter(img => img.key !== image.key));
        } else {
            setSelectedGalleryImages([...selectedGalleryImages, image]);
        }
    };

    // Selecionar/Deselecionar todas
    const toggleSelectAll = () => {
        if (selectedGalleryImages.length === galleryImages.length) {
            setSelectedGalleryImages([]);
        } else {
            setSelectedGalleryImages([...galleryImages]);
        }
    };

    // Template para item da galeria
    const itemTemplate = (image) => {
        const isSelected = selectedGalleryImages.some(img => img.key === image.key);

        return (
            <div className="col-12 sm:col-6 lg:col-4 xl:col-3 p-2">
                <Card className={`border-1 surface-border relative gallery-item ${isSelected ? 'border-primary' : ''}`}>
                    <div className="flex flex-column align-items-center gap-3">
                        <div style={{ position: 'relative', width: '100%', height: '200px', overflow: 'hidden' }}>
                            <div className="absolute top-0 left-0 p-2 z-1">
                                <Checkbox
                                    checked={isSelected}
                                    onChange={() => toggleImageSelection(image)}
                                    className="bg-white-alpha-80"
                                />
                            </div>

                            <Image
                                src={image.url}
                                alt={image.name}
                                preview
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    cursor: 'pointer'
                                }}
                                onError={(e) => {
                                    e.target.src = '/placeholder-image.png';
                                }}
                            />

                        </div>
                    </div>
                    <div className="p-2">
                        <div className="text-sm font-medium text-overflow-ellipsis overflow-hidden white-space-nowrap">
                            {image.name}
                        </div>
                        <div className="text-xs text-500 mt-1">
                            {image.size ? `${(image.size / 1024).toFixed(1)} KB` : ''}
                        </div>
                        <Button
                            icon="pi pi-trash"
                            className="p-button-rounded p-button-danger p-button-text mt-2"
                            onClick={() => handleDeleteSingle(image)}
                            tooltip="Excluir imagem"
                            tooltipOptions={{ position: 'bottom' }}
                        />
                    </div>
                </Card>
            </div>
        );
    };

    // Toolbar da galeria
    const galleryToolbar = () => {
        return (
            <Toolbar
                left={
                    <div className="flex align-items-center gap-2">
                        <Button
                            label="Selecionar Todas"
                            icon={selectedGalleryImages.length === galleryImages.length ? 'pi pi-check-square' : 'pi pi-square'}
                            className="p-button-text"
                            onClick={toggleSelectAll}
                            disabled={galleryImages.length === 0}
                        />
                        <Divider layout="vertical" />
                        <Button
                            label="Excluir Selecionadas"
                            icon="pi pi-trash"
                            className="p-button-danger"
                            onClick={handleDeleteSelected}
                            disabled={selectedGalleryImages.length === 0}
                        >
                            {selectedGalleryImages.length > 0 && (
                                <Badge value={selectedGalleryImages.length} className="ml-2" />
                            )}
                        </Button>
                    </div>
                }
                right={
                    <div className="text-500">
                        {galleryImages.length} imagem(ns) na pasta
                    </div>
                }
            />
        );
    };

    return (
        <div className="p-4">
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* Seção de Upload */}
            <Panel header="Upload de Imagens" className="mb-4">
                <div className="grid">
                    <div className="col-12 md:col-6 mb-3">
                        <label className="block mb-2 font-medium">Pasta de Destino</label>
                        <Dropdown
                            value={uploadFolder}
                            options={folders}
                            onChange={(e) => setUploadFolder(e.value)}
                            placeholder="Selecione a pasta para upload"
                            className="w-full"
                            showClear
                        />
                    </div>

                    <div className="col-12">
                        <FileUpload
                            ref={fileUploadRef}
                            name="images"
                            multiple
                            accept="image/*"
                            maxFileSize={30000000}
                            customUpload
                            uploadHandler={handleUpload}
                            onSelect={onFileSelect}
                            onClear={() => setSelectedFiles([])}
                            emptyTemplate={
                                <div className="flex align-items-center justify-content-center flex-column">
                                    <i className="pi pi-image mt-3 p-5" style={{ fontSize: '5em', borderRadius: '50%', backgroundColor: 'var(--surface-b)', color: 'var(--surface-d)' }}></i>
                                    <span style={{ fontSize: '1.2em', color: 'var(--text-color-secondary)' }} className="my-5">
                                        Arraste e solte as imagens aqui
                                    </span>
                                </div>
                            }
                            progressBarTemplate={
                                uploading && <ProgressBar value={uploadProgress} />
                            }
                            headerTemplate={(options) => {
                                const { chooseButton, uploadButton, cancelButton } = options;
                                return (
                                    <div className="flex flex-wrap justify-content-between align-items-center w-full">
                                        <div className="flex gap-4 mb-2">
                                            <div className="mr-2">
                                                {chooseButton}
                                            </div>
                                            <div className="mr-2">
                                                <Button
                                                    label="Upload"
                                                    icon="pi pi-upload"
                                                    onClick={handleUpload}
                                                    disabled={!uploadFolder || selectedFiles.length === 0 || uploading}
                                                    loading={uploading}
                                                />
                                            </div>
                                            {cancelButton}
                                        </div>
                                        <div className="text-500 mt-4">
                                            {selectedFiles.length > 0 && `${selectedFiles.length} arquivo(s) selecionado(s)`}
                                        </div>
                                    </div>
                                );
                            }}
                        />
                    </div>
                </div>
            </Panel>

            <Divider />

            {/* Seção da Galeria */}
            <Panel header="Galeria de Imagens" className="mt-4">
                <div className="mb-3">
                    <label className="block mb-2 font-medium">Selecione a Pasta</label>
                    <Dropdown
                        value={selectedFolder}
                        options={folders}
                        onChange={(e) => setSelectedFolder(e.value)}
                        placeholder="Escolha uma pasta para visualizar"
                        className="w-full md:w-25rem"
                        showClear
                    />
                </div>

                {selectedFolder && (
                    <>
                        {loading ? (
                            <div className="flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                                <ProgressSpinner />
                            </div>
                        ) : error ? (
                            <Message severity="error" text={error} className="w-full" />
                        ) : galleryImages.length === 0 ? (
                            <div className="flex flex-column align-items-center justify-content-center p-5">
                                <i className="pi pi-inbox" style={{ fontSize: '3rem', color: 'var(--text-color-secondary)' }}></i>
                                <p className="text-lg text-color-secondary mt-3">Nenhuma imagem encontrada nesta pasta</p>
                            </div>
                        ) : (
                            <>
                                {galleryToolbar()}
                                <DataView
                                    value={galleryImages}
                                    itemTemplate={itemTemplate}
                                    layout="grid"
                                    className="mt-3"
                                />
                            </>
                        )}
                    </>
                )}
            </Panel>
        </div>
    );
};

export default ImageGallery;